/*
| Every public REST path the explorer reads, in one place.
|
| Names and query keys mirror the TypeScript SDK's route table
| (typescript-sdks/packages/core/src/api/perp/routes.ts) so the two can be read
| side by side. tests/openapi.test.mjs checks each path against the generated
| OpenAPI spec, so a renamed endpoint fails here before it fails in a browser.
*/

export const API_V1 = '/api/v1';

const seg = (value) => encodeURIComponent(String(value));

/** Appends the defined params as a query string, in insertion order. */
export function withQuery(path, params) {
  const query = Object.entries(params ?? {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  return query ? `${path}?${query}` : path;
}

export const routes = {
  market: {
    pairs: (params) => withQuery(`${API_V1}/market/pairs`, params),
    screener: (params) => withQuery(`${API_V1}/market/screener`, params),
    metadata: (id) => `${API_V1}/market/pairs/${seg(id)}/metadata`,
    perpSummary: (id) => `${API_V1}/market/pairs/${seg(id)}/perp/summary`,
    candles: (id, interval, params) =>
      withQuery(`${API_V1}/market/pairs/charts/candlestick/${seg(id)}/${seg(interval)}`, params),
    liquidationMetrics: (id, params) => withQuery(`${API_V1}/market/pairs/${seg(id)}/liquidation-metrics`, params),
    liquidationEvents: (params) => withQuery(`${API_V1}/market/liquidation-events`, params),
    insuranceFunds: () => `${API_V1}/market/insurance-funds`,
  },
  trades: {
    byPair: (id, params) => withQuery(`${API_V1}/trades/${seg(id)}`, params),
    feed: (params) => withQuery(`${API_V1}/trades`, params),
  },
  fees: {
    /** A pair's fee tier schedule; replaces the flat pair fee fields removed in #5701. */
    schedule: (params) => withQuery(`${API_V1}/fees/schedule`, params),
  },
  analytics: {
    protocolTotals: (params) => withQuery(`${API_V1}/analytics/protocol/totals`, params),
    protocolSeries: (params) => withQuery(`${API_V1}/analytics/protocol/series`, params),
    marketVolumes: (params) => withQuery(`${API_V1}/analytics/markets/volume`, params),
    treasuryNetFees: () => `${API_V1}/analytics/treasury/net-fees`,
    builders: () => `${API_V1}/analytics/builders`,
    builder: (key) => `${API_V1}/analytics/builders/${seg(key)}`,
    builderTotals: (key, params) => withQuery(`${API_V1}/analytics/builders/${seg(key)}/totals`, params),
    builderSeries: (key, params) => withQuery(`${API_V1}/analytics/builders/${seg(key)}/series`, params),
    builderLeaderboard: (params) => withQuery(`${API_V1}/analytics/builder-leaderboard`, params),
    tokens: () => `${API_V1}/analytics/tokens`,
    token: (token) => `${API_V1}/analytics/tokens/${seg(token)}`,
    walletLeaderboard: (params) => withQuery(`${API_V1}/analytics/wallet-leaderboard`, params),
    wallet: (wallet) => `${API_V1}/analytics/wallets/${seg(wallet)}`,
    walletLiquidations: (wallet, params) => withQuery(`${API_V1}/analytics/wallets/${seg(wallet)}/liquidations`, params),
    // `token` and `wallet` filters arrive with 0XM-3663; see models/transfers.js.
    deposits: (params) => withQuery(`${API_V1}/analytics/deposits`, params),
    withdrawals: (params) => withQuery(`${API_V1}/analytics/withdrawals`, params),
  },
};
